import {
  BackupPolicyType,
  BackupStatus,
  ConfigurationStatus,
  type InventoryResource,
  ResourceBlockerType,
} from '@repo/api-gw-sdk';

import { isUnbackableStatus } from '@/components/backupStatus/createBackupStatusNotInCondition';

// 'never' is a reason the server records as a blocker but never projects as action required.
type ActionRequiredGate = 'none' | 'anyPolicy' | 'pitrPolicy' | 'never';

type ActionRequiredBlockerType = Extract<
  ResourceBlockerType,
  `ACTION_REQUIRED_${string}`
>;

/**
 * What each action-required reason waits for before it makes a resource read as needing action.
 * Reasons gated on an attached policy stay hidden on an unassigned resource, so a blocker alone does
 * not mean the customer has anything to do yet. Keyed by every ACTION_REQUIRED_* blocker type, so a
 * type added to the API fails the type check until it is given the gate the server applies.
 */
const ACTION_REQUIRED_GATES: Record<
  ActionRequiredBlockerType,
  ActionRequiredGate
> = {
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_SQL_VM_INVALID_CONFIGURATION]:
    'pitrPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_SAP_HANA_BACKUP_NOT_CONFIGURED]:
    'pitrPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_GCP_SQL_VM_BACKUP_NOT_CONFIGURED]:
    'pitrPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_GCP_SQL_VM_BACKUP_APPROACHING_CAPACITY]:
    'pitrPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_GCP_SQL_VM_INVALID_CONFIGURATION]:
    'pitrPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_INVALID_STREAM_VIEW_TYPE]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_PITR_NOT_ENABLED]: 'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_INSUFFICIENT_RCUS]: 'none',
  [ResourceBlockerType.ACTION_REQUIRED_RDS_DATABASE_STOPPED]: 'none',
  [ResourceBlockerType.ACTION_REQUIRED_OBJECT_STORE_INVENTORY_REQUIRED]: 'none',
  [ResourceBlockerType.ACTION_REQUIRED_OBJECT_STORE_INVENTORY_SCP_DENIED_PUBLIC_ACCESS_BLOCK]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_CLOUD_SQL_DATABASE_STOPPED]: 'none',
  [ResourceBlockerType.ACTION_REQUIRED_BIGQUERY_CHANGE_HISTORY_DISABLED]:
    'none',
  [ResourceBlockerType.ACTION_REQUIRED_RDS_GLOBAL_DB_SECONDARY_CLUSTER]: 'none',
  [ResourceBlockerType.ACTION_REQUIRED_KEYSPACES_CDC_NOT_ENABLED]: 'none',
  [ResourceBlockerType.ACTION_REQUIRED_AWS_NATIVE_PITR_PERMISSIONS_MISSING]:
    'none',
  [ResourceBlockerType.ACTION_REQUIRED_RDS_MANAGED_BY_EXTERNAL_BACKUP_PLAN]:
    'none',
  [ResourceBlockerType.ACTION_REQUIRED_SAP_HANA_AGENT_APPLICATION_NOT_ONBOARDED]:
    'none',
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_FILES_STORAGE_ACCOUNT_IN_EXTERNAL_VAULT]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_FILES_BACKUP_PERMISSION_MISSING]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_FILES_BACKUP_NETWORK_ACCESS_BLOCKED]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_VM_NATIVE_BACKUP_PERMISSION_MISSING]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_STREAMS_PERMISSIONS_MISSING]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_EXPORT_PERMISSIONS_MISSING]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_INCREMENTAL_EXPORT_REQUIRES_EXPORT_BASE]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_KMS_KEY_INACCESSIBLE]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_SHOVAL_AGENT_NOT_RUNNING]: 'none',
  [ResourceBlockerType.ACTION_REQUIRED_SHOVAL_AGENT_MISSING_PERMISSIONS]:
    'none',
  [ResourceBlockerType.ACTION_REQUIRED_SHOVAL_AGENT_REJECTED_ATTACH]: 'none',
  [ResourceBlockerType.ACTION_REQUIRED_SHOVAL_AGENT_NO_HF_POLICY]: 'none',
  [ResourceBlockerType.ACTION_REQUIRED_AWS_NATIVE_BACKUP_PERMISSIONS_MISSING]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_AWS_BACKUP_RESOURCE_TYPE_NOT_OPTED_IN]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_EKS_CLUSTER_CONFIGMAP_ONLY_AUTH]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_GCP_VM_IMAGE_SHARING_DOMAIN_RESTRICTED]:
    'none',
  [ResourceBlockerType.ACTION_REQUIRED_KEYSPACES_INSUFFICIENT_RCUS]: 'never',
  [ResourceBlockerType.ACTION_REQUIRED_PERMISSION_GROUP_NOT_GRANTED]:
    'anyPolicy',
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_STORAGE_SCAN_PERMISSION_MISSING]:
    'none',
};

/** The action-table row each configuration status reads as, before DLSG and action-required. */
const ACTION_STATUS_BY_CONFIGURATION: Record<
  ConfigurationStatus,
  BackupStatus
> = {
  [ConfigurationStatus.CONFIGURATION_STATUS_TERMINATED]:
    BackupStatus.Terminated,
  [ConfigurationStatus.CONFIGURATION_STATUS_DISCONNECTED]:
    BackupStatus.Disconnected,
  [ConfigurationStatus.CONFIGURATION_STATUS_EXCLUDED_FROM_BACKUP]:
    BackupStatus.ExcludedFromBackup,
  [ConfigurationStatus.CONFIGURATION_STATUS_LIMIT_EXCEEDED]:
    BackupStatus.LimitExceeded,
  [ConfigurationStatus.CONFIGURATION_STATUS_UNSUPPORTED]:
    BackupStatus.Unsupported,
  [ConfigurationStatus.CONFIGURATION_STATUS_ACCESS_LOST]:
    BackupStatus.AccessLost,
  [ConfigurationStatus.CONFIGURATION_STATUS_CLASSIFICATION_FAILED]:
    BackupStatus.ClassificationFailed,
  [ConfigurationStatus.CONFIGURATION_STATUS_INITIAL_CLASSIFICATION]:
    BackupStatus.InitialClassification,
  [ConfigurationStatus.CONFIGURATION_STATUS_UNASSIGNED]:
    BackupStatus.NotBackedUp,
  [ConfigurationStatus.CONFIGURATION_STATUS_ASSIGNED]: BackupStatus.Protected,
};

const hasActionRequiredBlocker = (resource: InventoryResource): boolean => {
  const isAssigned =
    resource.configurationStatus ===
    ConfigurationStatus.CONFIGURATION_STATUS_ASSIGNED;
  const hasPitrPolicy = resource.backupPolicies.some(
    (policy) => policy.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR
  );

  return (resource.statusProperties?.blockers ?? []).some((blocker) => {
    const gate = blocker.statusMessageKey
      ? ACTION_REQUIRED_GATES[
          blocker.statusMessageKey as ActionRequiredBlockerType
        ]
      : undefined;
    switch (gate) {
      case 'none':
        return true;
      case 'anyPolicy':
        return isAssigned;
      case 'pitrPolicy':
        return hasPitrPolicy;
      default:
        return false;
    }
  });
};

/**
 * The action-table row a resource's actions and eligibility are decided from. With the status
 * columns off it is the posture. With them on it is derived from the configuration status, a DLSG
 * policy and the action-required blockers, and a resource whose configuration status the server
 * has not computed yet keeps its posture. A candidate (unassigned or assigned) reads DLSG ahead of
 * action-required, the way the server ranks them.
 */
export const getActionStatus = (
  resource: InventoryResource,
  isStatusColumnsEnabled: boolean | undefined
): BackupStatus => {
  const configurationStatus = resource.configurationStatus;
  if (!isStatusColumnsEnabled || !configurationStatus) {
    return resource.backupStatus;
  }

  const isCandidate =
    configurationStatus ===
      ConfigurationStatus.CONFIGURATION_STATUS_UNASSIGNED ||
    configurationStatus === ConfigurationStatus.CONFIGURATION_STATUS_ASSIGNED;
  if (isCandidate && resource.dlsgPolicy) {
    return BackupStatus.Dlsg;
  }
  if (isCandidate && hasActionRequiredBlocker(resource)) {
    return BackupStatus.ActionRequired;
  }
  return ACTION_STATUS_BY_CONFIGURATION[configurationStatus];
};

export const isDlsgResource = (
  resource: InventoryResource,
  isStatusColumnsEnabled: boolean | undefined
): boolean =>
  getActionStatus(resource, isStatusColumnsEnabled) === BackupStatus.Dlsg;

export const isActionRequiredResource = (
  resource: InventoryResource,
  isStatusColumnsEnabled: boolean | undefined
): boolean =>
  getActionStatus(resource, isStatusColumnsEnabled) ===
  BackupStatus.ActionRequired;

export const isExcludedResource = (
  resource: InventoryResource,
  isStatusColumnsEnabled: boolean | undefined
): boolean =>
  getActionStatus(resource, isStatusColumnsEnabled) ===
  BackupStatus.ExcludedFromBackup;

export const isUnbackableResource = (
  resource: InventoryResource,
  isStatusColumnsEnabled: boolean | undefined
): boolean =>
  isUnbackableStatus(getActionStatus(resource, isStatusColumnsEnabled));
